import React from 'react';
import { User } from 'services/AuthenticationService';
import { UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';
import jwt from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import * as storageManager from 'utilities/sessionStorage'
import { useCookies } from 'react-cookie';

const config: UserManagerSettings = {
    // the URL of our identity server
    authority: process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL : '',
    // this ID maps to the client ID in the identity client configuration
    client_id: "Veiligheidsverificaties_App",
    // URL to redirect to after login
    // redirect_uri: "http://localhost:3000/login",
    redirect_uri: `${document.location.origin}/login`,
    response_type: "code",
    // the scopes or resources we would like access to
    scope: "openid profile Veiligheidsverificaties",
    // URL to redirect to after logout
    // post_logout_redirect_uri: "https://localhost:3000/login",
    post_logout_redirect_uri: `${document.location.origin}/login`,
    stateStore: new WebStorageStateStore({ store: window.localStorage }),
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    filterProtocolClaims: false,
};

interface IAuthContext {
    user: User | null;
    loading: boolean;
    setUser: React.Dispatch<React.SetStateAction<User | null>>;
    saveUser: (user: User) => void;
    login: (isLocal?: boolean) => void;
    logout: (redirectUri?: string) => void;
    updateUserNames: (firstName: string, lastName: string) => void,
    checkIfUserLoginWasSucces: (redirectUri: string) => void;
    getUser: (location?: string) => Promise<boolean>;
    checkAuth: (requiredRoles: string[], inAllRoles?: boolean, onlyRoles?: boolean) => boolean;
    isAuthenticated: () => boolean;
}

export const AuthContext = React.createContext<IAuthContext>({
    user: null,
    loading: false,
    setUser: () => null,
    saveUser: () => null,
    login: () => null,
    logout: () => null,
    updateUserNames: () => null,
    checkIfUserLoginWasSucces: () => null,
    getUser: () => new Promise<boolean>(() => { }),
    checkAuth: () => false,
    isAuthenticated: () => false,
});

export const AuthProvider = ({ children }: any) => {
    const [user, setUser] = React.useState<User | null>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [_, setCookies] = useCookies();
    const navigate = useNavigate();

    const login = async (isLocal: boolean = false) => {
        const userManager = new UserManager(config)
        await userManager.signinRedirect({ extraQueryParams: { external: isLocal.toString() } });
        userManager.events.addAccessTokenExpiring(() => {
            userManager.signinSilent({ extraQueryParams: { external: false } });
        });
    }

    const checkIfUserLoginWasSucces = async (redirectUri: string) => {
        const userManager = new UserManager(config);
        if (userManager === undefined) return;
        userManager.signinRedirectCallback().then(async (user) => {
            if (user) {
                localStorage.setItem('token', user?.access_token as string);
                const test = convertUser(user);
                setCookies('__tenant', test.tenantId, { path: '/', sameSite: 'none' })
                localStorage.setItem('user', JSON.stringify(test));
                user.profile.exp = test.exp;
                await userManager.storeUser(user);
                setUser(test)
                navigate(redirectUri);
            } else {
                return
            }
        }).catch(() => {
        });
    }

    const getUser = async (location: string = ''): Promise<boolean> => {
        const userManager = new UserManager(config)
        const storageUser = storageManager.getLocalStorage<User>('user')
        const getUserAsync = async () => {
            const newUser = await userManager.getUser()
                .then((user) => {
                    return convertUser(user)
                })
                .catch((_) => {
                    return {} as User
                });
            console.log('getUser', newUser)
            if (Object.keys(newUser).length === 0) {
                setUser(null)
                return false;

            } else {
                if (new Date(newUser.exp * 1000) < new Date()) {
                    console.log('token expired')
                    userManager.removeUser()
                    localStorage.removeItem('user');
                    localStorage.removeItem('token');
                    setUser(null)
                    return false;
                }
                setCookies('__tenant', newUser.tenantId, { path: '/', sameSite: 'none', secure: true })
                setUser(newUser)
                return true
            }
        }
        let result: boolean = false;
        if (user === null && storageUser !== null) {
            if (isAuthenticated(storageUser)) {
                setUser(storageUser)
                result = true;
            } else {
                result = await getUserAsync()
            }
        } else if (storageUser === null && user === null) {
            result = await getUserAsync()
        }
        if (!result) return false;
        if (location === 'login') return true
        return false
    }

    const saveUser = (_user: User) => {
        const userManager = new UserManager(config)
        setUser(_user)
        localStorage.setItem('user', JSON.stringify(_user));
        storageManager.pasrseLocalStorage<User>('user', _user);
        userManager.getUser()
            .then((user) => {
                if (user) {
                    user.profile.role = _user.role;
                    userManager.storeUser(user);
                }
            })

    }

    const getRoles = (): string[] => {
        if (user === null) return [];
        return user.role
    }

    const checkAuth = (requiredRoles: string[], inAllRoles: boolean = true, onlyRoles: boolean = false): boolean => {
        if (user === null) return false;
        if (!isAuthenticated()) return false;
        if (!user.role) {
            return false;
        }
        const roles = getRoles();
        if (roles.length === 0) {
            return false
        };

        if (onlyRoles) {
            if (requiredRoles.length == roles.length && requiredRoles.every(r => roles.some((s: string) => s === r))) {
                return true;
            }
            return false;
        }

        if (inAllRoles) {
            if (requiredRoles.every(r => roles.some((s: string) => s === r))) {
                return true;
            }
        }
        else {
            if (requiredRoles.some((item) => roles.includes(item))) {
                return true;
            }
        }


        return false;
    }

    const isAuthenticated = (testUser: User | null = user): boolean => {
        if (testUser) {
            const expired: boolean = !isTokenExpired(testUser);
            return expired;
        }
        return false;
    }

    const isTokenExpired = (testUser: User | null = user): boolean => {
        if (testUser === null) return true;
        const now = Date.now() / 1000;
        if (now < testUser.exp) return false;

        return true
    }

    const logout = (redirectUri: string = '/login') => {
        const userManager = new UserManager(config)
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        userManager.signoutRedirect({ post_logout_redirect_uri: redirectUri, extraQueryParams: { redirectUri: redirectUri } });
    }

    const updateUserNames = (firstName: string, lastName: string) => {
        if (user) {
            user.given_name = firstName;
            user.surname = lastName;
            saveUser(user);
        }
        
    }

    React.useEffect(() => {
        console.log('useEffect')
        getUser().then(() => {
            setLoading(true)
        });
    }, []);

    const value = {
        user,
        loading,
        setUser,
        saveUser,
        login,
        logout,
        updateUserNames,
        checkIfUserLoginWasSucces,
        getUser,
        checkAuth,
        isAuthenticated
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )

}

export const useAuthContext = () => React.useContext(AuthContext);

function convertUser(user: any): User {
    console.log(user)
    const profile = user?.profile;
    const access_token = jwt<any>(user?.access_token as string);
    console.log(profile);
    const test: User = {
        email: profile?.unique_name,
        name: profile?.given_name,
        surname: profile?.family_name as string,
        given_name: profile?.given_name as string,
        role: access_token.role ? typeof access_token.role === 'string' ? [access_token.role] : access_token.role : [],
        loginProvider: '',
        nbf: profile?.nbf,
        exp: access_token?.exp as number,
        iat: profile?.iat as number,
        tenantId: profile?.tenantid as string,
    }
    // alert(test.tenantId)
    return test;
}
